import React from 'react';
import PocketField from './PocketField';
import InfoHint from '../../components/common/InfoHint';
import { buildTpExits } from '../../utils/multiExits';

// matches FuturesMetricsCard / FuturesSettingsModal default per-side fees
const FUT_FEES = { broker: 2.00, exchange: 1.32, nfa: 0.02, clearing: 0.20 };

const ROW = 'flex justify-between items-center gap-3 px-4 py-1.5 text-[12.5px]';
const LBL = 'text-[#7a8492] shrink-0';
const MICRO = 'text-[#5b6573] text-[10px]';
const TOG = (on) => `text-[10.5px] px-2 py-0.5 rounded transition-colors ${on ? 'text-white bg-[#D97706]' : 'text-[#6b7480] bg-white/5 hover:bg-white/10'}`;

const Header = ({ children }) => (
  <div className="text-[#bd9263] text-[9px] uppercase tracking-[0.13em] px-4 pt-3 pb-1 font-medium">{children}</div>
);
const Seg = ({ options, value, onChange }) => (
  <span className="flex gap-0.5 shrink-0">
    {options.map((o) => (
      <button key={String(o.value)} onClick={() => onChange(o.value)} className={TOG(value === o.value)}>{o.label}</button>
    ))}
  </span>
);
const Results = ({ rows }) => (
  <div className="mx-4 mt-1.5 mb-1 rounded-lg bg-white/[0.025] p-2.5 space-y-1">
    {rows.map((r) => (
      <div key={r.label} className="flex justify-between gap-3 text-[11px]">
        <span className="text-[#5b6573]">{r.label}</span>
        <span className={`tabular-nums text-right ${r.bold ? 'text-[#d9a441] font-semibold' : 'text-[#9aa7b4]'}`}>{r.value ?? '--'}</span>
      </div>
    ))}
  </div>
);

// Per-market settings sheet — mirrors each market's real SettingsModal (combined
// toggle+input rows, conditional fields, read-only fee breakdown). Edits the same
// hook state the active view reads (setField provided by PocketCalc).
const PocketSettings = ({ market, values, metrics, setField, onClose }) => {
  const fb = metrics?.feeBreakdown || {};

  // helper renderers (functions returning JSX, NOT components — keeps PocketField
  // mounted across renders so typing doesn't lose focus)
  const num = ({ label, k, plain, ...p }) => (
    <div className={ROW}>
      <span className={LBL}>{label}</span>
      <PocketField value={values[k]} onCommit={(bn) => setField(k, plain ? bn.toNumber() : bn)} align="right" {...p} />
    </div>
  );
  const riskRow = () => (
    <div className={ROW}>
      <span className={LBL}>Risk</span>
      <span className="flex items-center gap-2">
        <PocketField value={values.risk} onCommit={(bn) => setField('risk', bn)} decimals={2} align="right" />
        <Seg options={[{ value: 'fixed', label: '$' }, { value: 'percent', label: '%' }]} value={values.riskUnit} onChange={(u) => setField('riskUnit', u)} />
      </span>
    </div>
  );
  const bufferRow = () => (
    <div className="px-4 py-1.5">
      <div className="flex justify-between items-center text-[12.5px]">
        <span className={LBL}>Safety Buffer</span>
        <PocketField value={values.safetyBufferR} onCommit={(bn) => setField('safetyBufferR', bn)} suffix="R" decimals={2} min={0.25} align="right" />
      </div>
      <div className="text-[#5b6573] text-[10px] mt-0.5">Keeps liquidation this many stop-distances beyond your stop — 2R = two stops past it.</div>
    </div>
  );
  const topRisk = () => (<>
    <Header>Risk</Header>
    {riskRow()}
    {num({ label: 'Account Balance', k: 'accountBalance', prefix: '$', decimals: 2 })}
    {num({ label: 'Nominal R:R', k: 'nominalRR', suffix: 'x', decimals: 1, min: 0.1 })}
    {num({ label: 'Max Leverage', k: 'maxLeverage', suffix: 'x', decimals: 0, min: 1 })}
  </>);

  // --- Multiple take-profits (shared across all markets) ---
  const exits = Array.isArray(values.tpExits) ? values.tpExits : [];
  const setExit = (i, field, bn) => {
    const next = exits.map((e, idx) => (idx === i ? { ...e, [field]: bn } : e));
    setField('tpExits', next);
  };
  const setTpCount = (n) => { setField('tpCount', n); setField('tpExits', buildTpExits(n)); };
  const allocSum = exits.slice(0, values.tpCount).reduce((a, e) => {
    const v = e?.alloc;
    return a + (v && v.toNumber ? v.toNumber() : Number(v) || 0);
  }, 0);
  const multiTpSection = () => (<>
    <Header>Multiple take-profits</Header>
    <div className={ROW}>
      <span className={`${LBL} flex items-center gap-1`}>
        Scale out
        <InfoHint text="Split the position across 2–5 exits. Each target's R is net — fees included (the optimized take), so the calculator shows only the optimized price." />
      </span>
      <Seg options={[{ value: false, label: 'Single' }, { value: true, label: 'Multiple' }]} value={!!values.multiTpEnabled} onChange={(v) => setField('multiTpEnabled', v)} />
    </div>
    {values.multiTpEnabled && (<>
      <div className={ROW}>
        <span className={LBL}>Exits</span>
        <Seg options={[2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }))} value={values.tpCount} onChange={setTpCount} />
      </div>
      {exits.slice(0, values.tpCount).map((e, i) => (
        <div className={ROW} key={i}>
          <span className={LBL}>TP {i + 1}</span>
          <span className="flex items-center gap-2">
            <PocketField value={e.alloc} onCommit={(bn) => setExit(i, 'alloc', bn)} suffix="%" decimals={0} align="right" />
            <span className={MICRO}>@</span>
            <PocketField value={e.rMultiple} onCommit={(bn) => setExit(i, 'rMultiple', bn)} suffix="R" decimals={2} min={0.1} align="right" />
          </span>
        </div>
      ))}
      <div className="px-4 pb-1.5 text-[10px] text-right">
        <span className={Math.abs(allocSum - 100) < 0.5 ? 'text-[#54cca0]' : 'text-[#d9a441]'}>
          {allocSum < 100 ? `Allocated ${allocSum}% · runner ${100 - allocSum}%` : allocSum > 100 ? `Allocated ${allocSum}% — over 100%` : 'Allocated 100%'}
        </span>
      </div>
    </>)}
  </>);

  let body = null;

  if (market === 'Crypto') {
    body = (<>
      {topRisk()}
      {num({ label: 'Entry Fee', k: 'entryFee', suffix: '%', decimals: 5 })}
      {num({ label: 'Exit Fee', k: 'exitFee', suffix: '%', decimals: 5 })}
      {bufferRow()}
      {multiTpSection()}
    </>);
  } else if (market === 'Forex') {
    const isPctComm = values.commType === 'percent';
    body = (<>
      {topRisk()}
      {bufferRow()}
      {multiTpSection()}
      <Header>Fee breakdown</Header>
      {num({ label: 'Spread', k: 'spread', suffix: 'p', decimals: 1 })}
      <div className={ROW}>
        <span className={LBL}>Commission</span>
        <span className="flex items-center gap-2">
          <PocketField value={values.commRate} onCommit={(bn) => setField('commRate', bn)} prefix={isPctComm ? '' : '$'} suffix={isPctComm ? '%' : ''} decimals={isPctComm ? 5 : 2} align="right" />
          <Seg options={[{ value: 'percent', label: '%' }, { value: 'per_lot', label: '$/lot' }]} value={values.commType} onChange={(u) => setField('commType', u)} />
        </span>
      </div>
      {num({ label: 'Swap Long', k: 'swapLong', prefix: '$', decimals: 2, allowNegative: true })}
      {num({ label: 'Swap Short', k: 'swapShort', prefix: '$', decimals: 2, allowNegative: true })}
      <div className={ROW}>
        <span className={LBL}>Nights</span>
        <span className="flex items-center gap-2">
          <PocketField value={values.nights} onCommit={(bn) => setField('nights', bn)} decimals={0} align="right" />
          <button onClick={() => setField('wedEnabled', !values.wedEnabled)} className={TOG(!!values.wedEnabled)}>+Wed x3</button>
        </span>
      </div>
      <Results rows={[
        { label: 'Spread Cost', value: fb.spreadCost },
        { label: 'Entry Commission', value: fb.entryComm },
        { label: 'Exit Commission', value: fb.exitComm },
        { label: 'Swap', value: fb.swapCost },
        { label: 'Total Round Turn', value: fb.totalRoundTurn, bold: true },
      ]} />
    </>);
  } else if (market === 'Stocks') {
    const perShare = values.brokerMode === 'perShare';
    body = (<>
      {topRisk()}
      {bufferRow()}
      {multiTpSection()}
      <Header>Fee breakdown</Header>
      <div className="px-4 py-1.5">
        <div className="flex justify-between items-center text-[12.5px]">
          <span className={LBL}>Broker</span>
          <span className="flex items-center gap-2">
            <PocketField value={values.brokerRate} onCommit={(bn) => setField('brokerRate', bn)} prefix="$" decimals={perShare ? 4 : 2} align="right" />
            <Seg options={[{ value: 'perShare', label: '/share' }, { value: 'fixed', label: 'fixed' }]} value={values.brokerMode} onChange={(u) => setField('brokerMode', u)} />
          </span>
        </div>
        {perShare && (
          <div className="flex justify-end items-center gap-4 mt-1">
            <span className="flex items-center gap-1"><span className={MICRO}>Min $</span><PocketField value={values.brokerMin} onCommit={(bn) => setField('brokerMin', bn)} prefix="$" decimals={2} align="right" /></span>
            <span className="flex items-center gap-1"><span className={MICRO}>Max %</span><PocketField value={values.brokerMaxPct} onCommit={(bn) => setField('brokerMaxPct', bn)} suffix="%" decimals={1} align="right" /></span>
          </div>
        )}
      </div>
      {num({ label: 'Exchange $/sh', k: 'exchangeRate', prefix: '$', decimals: 4 })}
      <div className={ROW}>
        <span className={LBL}>Clearing $/sh</span>
        <span className="flex items-center gap-4">
          <PocketField value={values.clearingRate} onCommit={(bn) => setField('clearingRate', bn)} prefix="$" decimals={4} align="right" />
          <span className="flex items-center gap-1"><span className={MICRO}>Max %</span><PocketField value={values.clearingMaxPct} onCommit={(bn) => setField('clearingMaxPct', bn)} suffix="%" decimals={1} align="right" /></span>
        </span>
      </div>
      {num({ label: 'SEC /$1M', k: 'secRate', prefix: '$', decimals: 2 })}
      <div className={ROW}>
        <span className={LBL}>FINRA /1k sh</span>
        <span className="flex items-center gap-4">
          <PocketField value={values.finraRate} onCommit={(bn) => setField('finraRate', bn)} prefix="$" decimals={3} align="right" />
          <span className="flex items-center gap-1"><span className={MICRO}>Max $</span><PocketField value={values.finraMax} onCommit={(bn) => setField('finraMax', bn)} prefix="$" decimals={2} align="right" /></span>
        </span>
      </div>
      {num({ label: 'FINRA CAT /1k', k: 'finraCatRate', prefix: '$', decimals: 3 })}
      <Results rows={[
        { label: 'Broker Commission', value: fb.broker },
        { label: 'Exchange / ECN', value: fb.exchange },
        { label: 'Clearing Fee', value: fb.clearing },
        { label: 'SEC Fee', value: fb.sec },
        { label: 'FINRA TAF', value: fb.finra },
        { label: 'FINRA CAT', value: fb.finraCat },
        { label: 'Total Round Turn', value: fb.total, bold: true },
      ]} />
    </>);
  } else if (market === 'Futures') {
    const side = (x) => `$${x.toFixed(2)}/side x2 = $${(x * 2).toFixed(2)}`;
    const total = (FUT_FEES.broker + FUT_FEES.exchange + FUT_FEES.nfa + FUT_FEES.clearing) * 2;
    body = (<>
      <Header>Risk</Header>
      {riskRow()}
      {num({ label: 'Account Balance', k: 'accountBalance', prefix: '$', decimals: 2 })}
      {num({ label: 'Desired R:R', k: 'desiredRR', suffix: 'x', decimals: 1, min: 0.1 })}
      {num({ label: 'Round Turn Fee', k: 'roundTurnFee', prefix: '$', decimals: 2 })}
      {num({ label: 'Overshoot tol.', k: 'riskOvershootTolerance', suffix: '%', decimals: 0, plain: true })}
      {multiTpSection()}
      <Header>Fee breakdown</Header>
      <Results rows={[
        { label: 'Broker Commission', value: side(FUT_FEES.broker) },
        { label: 'Exchange Fee', value: side(FUT_FEES.exchange) },
        { label: 'NFA Fee', value: side(FUT_FEES.nfa) },
        { label: 'Clearing Fee', value: side(FUT_FEES.clearing) },
        { label: 'Total Round Turn', value: `$${total.toFixed(2)}/contract`, bold: true },
      ]} />
      <div className="text-[#5b6573] text-[10px] px-4 pb-1">Adjust Round Turn Fee above if your broker differs.</div>
    </>);
  }

  return (
    <div className="absolute inset-0 bg-black/55 z-30" onClick={onClose}>
      <div className="pk-scroll absolute left-0 right-0 bottom-0 bg-[#11161f] border-t border-white/10 rounded-t-2xl max-h-[90%] overflow-y-auto text-[12.5px]" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-4 py-3 sticky top-0 bg-[#11161f] z-10 border-b border-white/5">
          <span className="text-[13px] font-semibold text-[#e4e9ef]">Settings · {market}</span>
          <button onClick={onClose} className="text-[#6b7480] hover:text-[#cdd4de]">✕</button>
        </div>
        {body}
        <div className="h-5" />
      </div>
    </div>
  );
};

export default PocketSettings;
